import { AnimatePresence, motion } from "motion/react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import EmailIcon from "@mui/icons-material/EmailOutlined";
import PhoneIcon from "@mui/icons-material/PhoneOutlined";
import QrCodeIcon from "@mui/icons-material/QrCode2";
import CloseIcon from "@mui/icons-material/Close";
import { assets } from "@/lib/assets";
import { Reveal } from "@/components/Reveal";
import { PageBanner } from "@/components/PageBanner";
import { cardBase, container } from "@/lib/ui";

export function Contact() {
  const { t } = useTranslation();
  const [qrOpen, setQrOpen] = useState(false);

  return (
    <>
      {/* 4.2 Header */}
      <PageBanner
        eyebrow={t("contact:banner.eyebrow")}
        title={t("contact:banner.title")}
        subtitle={t("contact:banner.subtitle")}
        center
      />

      {/* 4.3 Contact cards */}
      <section className="bg-bg py-24 md:py-32">
        <div className={container}>
          <div className="mx-auto grid max-w-5xl gap-6 md:grid-cols-3">
            <Reveal>
              <a
                href="mailto:wangchenxi@furtherverse.com"
                className={`group flex h-full flex-col items-center justify-center ${cardBase} p-8 text-center hover:-translate-y-1.5 hover:border-brand/50 hover:shadow-[0_24px_60px_-24px_rgba(1,120,254,0.5)]`}
              >
                <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-gradient-to-br from-brand/25 to-brand-cyan/10 text-brand ring-1 ring-inset ring-brand/20 transition-transform duration-300 group-hover:scale-110">
                  <EmailIcon style={{ fontSize: 28 }} />
                </div>
                <h3 className="mt-5 text-lg font-semibold">{t("contact:email.label")}</h3>
                <p className="mt-2 break-all text-sm text-fg-muted">wangchenxi@furtherverse.com</p>
              </a>
            </Reveal>

            <Reveal delay={0.1}>
              <a
                href="tel:19121572744"
                className={`group flex h-full flex-col items-center justify-center ${cardBase} p-8 text-center hover:-translate-y-1.5 hover:border-brand/50 hover:shadow-[0_24px_60px_-24px_rgba(1,120,254,0.5)]`}
              >
                <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-gradient-to-br from-brand/25 to-brand-cyan/10 text-brand ring-1 ring-inset ring-brand/20 transition-transform duration-300 group-hover:scale-110">
                  <PhoneIcon style={{ fontSize: 28 }} />
                </div>
                <h3 className="mt-5 text-lg font-semibold">{t("contact:phone.label")}</h3>
                <p className="mt-2 text-sm text-fg-muted">191 2157 2744</p>
                <p className="mt-1 text-xs text-fg-faint">{t("contact:phone.role")}</p>
              </a>
            </Reveal>

            <Reveal delay={0.2}>
              <button
                type="button"
                onClick={() => setQrOpen(true)}
                className={`group flex h-full w-full flex-col items-center justify-center ${cardBase} p-8 text-center hover:-translate-y-1.5 hover:border-brand/50 hover:shadow-[0_24px_60px_-24px_rgba(1,120,254,0.5)]`}
              >
                <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-gradient-to-br from-brand/25 to-brand-cyan/10 text-brand ring-1 ring-inset ring-brand/20 transition-transform duration-300 group-hover:scale-110">
                  <QrCodeIcon style={{ fontSize: 28 }} />
                </div>
                <h3 className="mt-5 text-lg font-semibold">{t("contact:wechat.label")}</h3>
                <img
                  src={assets.wechatQr}
                  alt={t("contact:wechat.label")}
                  className="mt-4 h-28 w-28 rounded-lg object-contain transition-transform duration-300 group-hover:scale-105"
                />
                <p className="mt-3 text-xs text-fg-faint">{t("contact:wechat.hint")}</p>
              </button>
            </Reveal>
          </div>
        </div>
      </section>

      {/* QR lightbox */}
      <AnimatePresence>
        {qrOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setQrOpen(false)}
            className="fixed inset-0 z-[60] flex items-center justify-center bg-black/70 p-6 backdrop-blur-sm"
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              transition={{ type: "spring", stiffness: 320, damping: 28 }}
              onClick={(e) => e.stopPropagation()}
              className="relative w-full max-w-xs rounded-2xl bg-white p-5 shadow-2xl"
            >
              <button
                type="button"
                onClick={() => setQrOpen(false)}
                aria-label={t("lightbox.close")}
                className="absolute -right-3 -top-3 flex h-9 w-9 items-center justify-center rounded-full bg-[#0b0f14] text-white shadow-lg"
              >
                <CloseIcon style={{ fontSize: 20 }} />
              </button>
              <img
                src={assets.wechatQr}
                alt={t("contact:wechat.label")}
                className="mx-auto aspect-square w-full max-w-[16rem] object-contain"
              />
              <p className="mt-3 text-center text-sm font-medium text-[#0b0f14]">
                {t("contact:wechat.modalHint")}
              </p>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
